import { useEffect, useMemo, useState, type ReactNode } from "react";
import s from "./Header.module.scss";
import { AutoComplete, Input, Menu, MenuProps, Spin } from "antd";
import { DownOutlined } from "@ant-design/icons";
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AppDispatch, RootState } from "../../redux/store";
import { setLang } from "../../redux/InformationSlice";
import i18n from "../../i18n/i18n";
import { getApiLanguage, publicRequest } from "../../utils/utils";

export type Language = {
  value: string;
  label: string;
};
const languages: Language[] = [
  { value: "RU", label: "Русский" },
  { value: "KG", label: "Кыргыз" },
  { value: "UZ", label: "O‘zbek" },
  { value: "TJ", label: "Тоҷикӣ" },
];

type HeaderProps = {
  onContactsClick: () => void;
};

const Header = ({ onContactsClick }: HeaderProps) => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(languages[0]);
  const params = useParams();
  const dispatch: AppDispatch = useDispatch();
  const language = useSelector((state: RootState) => state.info.lang) || "ru";
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOptions, setSearchOptions] = useState<
    Array<{ value: string; label: ReactNode }>
  >([]);

  const toggleOpen = () => setOpen(!open);
  const selectLanguage = (lang: Language) => {
    setSelected(lang);
    setOpen(false);
    i18n.changeLanguage(lang?.value?.toLowerCase());
    dispatch(setLang(lang?.value?.toLowerCase()));
  };

  const [selectedKey, setSelectedKey] = useState<string>("ru-law");

  const location = useLocation();

  const items: MenuProps["items"] = [
    {
      key: "ru-laws",
      label: t("Законы России"),
      children: [
        {
          label: (
            <NavLink to="/russia">
              {t("Обязательное медицинское освидетельствование")}
            </NavLink>
          ),
          key: "/russia",
          children: [
            { key: "/russia/hiv/6", label: t("ВИЧ") },
            {
              key: "/russia/pas/4",
              label: t("Употребление психоактивных веществ"),
            },
            { key: "/russia/syphilis/2", label: t("Сифилис") },
            { key: "/russia/tuberculosis/3", label: t("Туберкулёз") },
          ],
        },
        {
          key: "ru-hiv-migrants",
          label: (
            <NavLink to="/russia/pre-departure/hiv/1">
              {t("Для мигрантов, живущих с ВИЧ")}
            </NavLink>
          ),
          children: [
            {
              key: "/russia/hiv/law-exceptions/1",
              label: t("Законное пребывание"),
            },
            {
              key: "/russia/hiv/visits/2",
              label: t("Краткосрочные визиты"),
            },
            {
              key: "/russia/hiv/migrants/trip/1",
              label: t("Подготовка к поездке"),
            },
            {
              key: "/russia/hiv/migrants/treatment/2",
              label: t("Получение лечения"),
            },
            {
              key: "/russia/hiv/migrants/analysis/3",
              label: t("Анализы в России"),
            },
          ],
        },
        {
          key: "/russia/pre-departure/tuberculosis/3",
          label: t("Для мигрантов с диагнозом туберкулез"),
        },
        {
          key: "/russia/pre-departure/syphilis/2",
          label: t("Для мигрантов с диагнозом сифилис"),
        },
        {
          key: "/russia/pre-departure/pas/4",
          label: t("Об употреблении психоактивных веществ"),
        },
      ],
    },

    {
      key: "kz-law",
      label: t("Законы Казахстана"),
      children: [
        {
          key: "/kazakhstan",
          label: (
            <NavLink to="/kazakhstan">
              {t("Заболевания, ограничивающие въезд")}
            </NavLink>
          ),
        },
        {
          key: "/kazakhstan/hiv/1",
          label: t("Для мигрантов, живущих с ВИЧ"),
          children: [
            {
              key: "/kazakhstan/hiv/trip/1",
              label: t("Подготовка к поездке"),
            },
            {
              key: "/kazakhstan/hiv/treatment/2",
              label: t("Получение лечения"),
            },
            {
              key: "/kazakhstan/hiv/analysis/3",
              label: t("Анализы в Казахстане"),
            },
          ],
        },
        {
          key: "/kazakhstan/tuberculosis/3",
          label: t("Для мигрантов с диагнозом туберкулез"),
        },
        {
          key: "/kazakhstan/syphilis/2",
          label: t("Для мигрантов с диагнозом сифилис"),
        },
        {
          key: "/kazakhstan/pas/4",
          label: t("Об употреблении психоактивных веществ"),
        },
      ],
    },

    {
      key: "other-countries",
      label: t("Другие страны"),
      children: [
        {
          key: "/otherCountries/advises/2",
          label: t("Путешествия и миграция с ВИЧ"),
        },
        {
          key: "/otherCountries/deportation/1",
          label: t("Страны, депортирующие иностранцев, живущих с ВИЧ"),
        },
        {
          key: "/otherCountries/restrictions",
          label: t("Ограничения на въезд для мигрантов, живущих с ВИЧ"),
        },
      ],
    },

    {
      key: "info-block",
      label: t("Информационный блок"),
      children: [
        {
          key: "/about/diseases",
          label: t("О заболеваниях"),
        },

        {
          key: "/kazakhstan/hiv/1",
          label: t("Где пройти обследования"),
          children: [
            { key: "/checkup/kyrgyzstan", label: t("Кыргызстан") },
            { key: "/checkup/tajikistan", label: t("Таджикистан") },
            { key: "/checkup/uzbekistan", label: t("Узбекистан") },
          ],
        },
        {
          key: "/links",
          label: t("Полезные ссылки"),
        },
      ],
    },
  ];

  const staticPages = useMemo(
    () => [
      { label: t("Главная"), path: "/" },
      { label: t("Полезные ссылки"), path: "/links" },
      { label: t("Оценить риск"), path: "/risk" },
      { label: t("Пройти тест"), path: "/test" },
      { label: t("Я в России"), path: "/russia" },
      { label: t("Я в Казахстане"), path: "/kazakhstan" },
      { label: t("Я в другой стране"), path: "/otherCountries" },
    ],
    [t],
  );

  useEffect(() => {
    const q = searchValue.trim();
    if (q.length < 2) {
      setSearchOptions([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const handle = window.setTimeout(async () => {
      try {
        const { data } = await publicRequest.get("/api/v1/search/", {
          params: { q, lang: getApiLanguage(language), limit: 10 },
        });

        const apiResults: Array<{ label: string; path: string }> =
          data?.results ?? [];

        const merged = [
          ...apiResults.map((r) => ({ label: r.label, path: r.path })),
          ...staticPages,
        ];

        const seen = new Set<string>();
        const options = merged
          .filter((r) => {
            const hay = `${r.label} ${r.path}`.toLowerCase();
            if (!hay.includes(q.toLowerCase())) return false;
            const key = `${r.label}::${r.path}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .slice(0, 10)
          .map((r) => ({
            value: r.path,
            label: (
              <div className={s.searchOption}>
                <div className={s.searchOptionTitle}>{r.label}</div>
              </div>
            ),
          }));

        setSearchOptions(options);
      } catch (e) {
        // Fallback: local pages only
        const options = staticPages
          .filter((r) => r.label.toLowerCase().includes(q.toLowerCase()))
          .slice(0, 10)
          .map((r) => ({
            value: r.path,
            label: (
              <div className={s.searchOption}>
                <div className={s.searchOptionTitle}>{r.label}</div>
              </div>
            ),
          }));
        setSearchOptions(options);
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(handle);
  }, [searchValue, language, staticPages]);

  return (
    <div className={s.header}>
      <Link to="/" className={s.logo}>
        <img src="/images/Main.svg" alt="Logo" className={s.logo} />
      </Link>
      <div>
        <Menu
          mode="horizontal"
          items={items}
          className={s.menu}
          onClick={({ key }) => navigate(key)}
          selectedKeys={[location.pathname]}
        />
      </div>
      <div className={s.search}>
        <AutoComplete
          value={searchValue}
          options={searchOptions}
          onSelect={(value) => {
            const path = String(value);
            setSearchValue("");
            setSearchOptions([]);
            if (path.startsWith("http://") || path.startsWith("https://")) {
              window.location.assign(path);
              return;
            }
            navigate(path);
          }}
          onSearch={(v) => setSearchValue(v)}
          popupClassName="vputi-search-dropdown"
          className={s.search_autocomplete}
          dropdownMatchSelectWidth={false}
        >
          <Input
            prefix={
              searchLoading ? (
                <Spin size="small" />
              ) : (
                <img src="/images/icon-search.svg" className={s.search_icon} />
              )
            }
            placeholder={t("Поиск")}
            variant="filled"
            className={s.search_input}
            allowClear
          />
        </AutoComplete>
      </div>
      <div className={s.lang}>
        <div className={s.dropdown}>
          <div
            className={`${s.container} ${open ? s.open : ""}`}
            onClick={toggleOpen}
          >
            <div className={s.header}>
              <span className={s.code}>{language.toUpperCase()}</span>
              <DownOutlined className={`${s.icon} ${open ? s.open : ""}`} />
            </div>

            <div className={s.options}>
              {languages.map((lang) => (
                <div
                  key={lang.value}
                  className={s.option}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectLanguage(lang);
                  }}
                >
                  {lang.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
